// @flow
import { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';

import {
  serializeKidsToQueryParam,
  formatDepartureDateForStore,
  getDepartureDateDisplayValue,
} from 'Utils/faceting/facetingUtils';
import redirectUtils from 'Utils/uri/redirectUtils';
import {
  QUERY_DEPARTURE_DATE,
  QUERY_DURATION,
  QUERY_KIDS,
  QUERY_TRANSPORT,
} from 'Utils/uri/constants';

type PersistCriteriaInUrlOptions = { criteriaKeysToPersist: Array<string> };

type Props = {
  adultsNumber: number,
  departureCity: string,
  departureDate: ?moment$Moment | number,
  duration: ?number,
  kids: Array<*>,
  kidsValidationState: Array<ValidationState>,
  uri: URI,
};

const FACET_QUERY_PARAMS = [QUERY_DEPARTURE_DATE, QUERY_DURATION, QUERY_KIDS, QUERY_TRANSPORT];
export const useSetUrlWithFacetings = (props: Props) => {
  const currentUrlHasNoQueryParams = (): boolean => {
    const currentQuery = props.uri.query(true);
    return FACET_QUERY_PARAMS.every(queryKey => isEmpty(currentQuery[queryKey]));
  };

  const computeQueryParamsForUrl = ({ criteriaKeysToPersist }: PersistCriteriaInUrlOptions) => {
    const { departureCity, departureDate, duration, kids, kidsValidationState } = props;
    const formattedDate =
      departureDate && getDepartureDateDisplayValue(formatDepartureDateForStore(departureDate, ''));

    return {
      [QUERY_DEPARTURE_DATE]:
        (criteriaKeysToPersist.includes(QUERY_DEPARTURE_DATE) && formattedDate) || undefined,
      [QUERY_DURATION]: (criteriaKeysToPersist.includes(QUERY_DURATION) && duration) || undefined,
      [QUERY_TRANSPORT]:
        (criteriaKeysToPersist.includes(QUERY_TRANSPORT) && departureCity) || undefined,
      [QUERY_KIDS]:
        (criteriaKeysToPersist.includes(QUERY_KIDS) &&
          kids.length > 0 &&
          serializeKidsToQueryParam({ kids, kidsValidationState })) ||
        undefined,
    };
  };

  const persistCriteriaInUrl = (options: PersistCriteriaInUrlOptions) => {
    // NOTE: URI.setQuery() does not remove existing queries
    const queryParams = computeQueryParamsForUrl(options);
    const searchPageUrl = props.uri
      .clone()
      .hash(null)
      .setQuery(queryParams);

    redirectUtils.redirectTo()(searchPageUrl, { shallow: true });
  };

  const isInitialMount = useRef(true);

  useEffect(
    () => {
      if (isInitialMount.current) {
        isInitialMount.current = false;

        if (currentUrlHasNoQueryParams()) {
          persistCriteriaInUrl({
            criteriaKeysToPersist: [
              QUERY_DEPARTURE_DATE,
              QUERY_DURATION,
              QUERY_TRANSPORT,
              QUERY_KIDS,
            ],
          });
        }
      } else {
        persistCriteriaInUrl({
          criteriaKeysToPersist: FACET_QUERY_PARAMS,
        });
      }
    },
    [props.departureDate, props.duration, props.departureCity, props.kids],
  );
};
